import {
  addDays,
  addMinutes,
  differenceInDays,
  format,
  isAfter,
  isFuture,
  isPast,
  subDays,
} from "date-fns";

import {
  getGroupIdFromAccessId,
  getStudentIdFromAccessId,
  isGroupAccessId,
  isStudentAccessId,
} from "../../../shared/access-id";
import { PresenceViewShortType } from "../../../shared/types/sharedb";
import { getUserShortName } from "../../../utils/user-utils";

import {
  EMPTY_DATE,
  formatDjangoDurationFieldToDaysCount,
} from "components/forms/due-date-form-helpers";
import type {
  ActivityType,
  PageType,
  RubricType,
  SubmissionType,
  TopicType,
} from "components/server-types";
import { ActivityCategory } from "components/server-types";
import { DEFAULT_SHORT_DATE_FORMAT, monthDayAtTime } from "i18n/helpers";
import { t } from "i18n/i18n";
import { ORDER_BY_CREATED, useCustomOrdered } from "mds/hooks/use-ordered";
import type { PageTabVariant } from "providers/DocViewProvider";
import { useAppSelector } from "store/index";
import { selectPageGroupsByPageId, selectUsersInCurrentCourse } from "store/selectors";
import type { UserWithSectionUserType } from "store/selectors/types";
import { useListSelector } from "store/store-hooks";
import { averageAndRound } from "utils/math";

export const indexToLetters = (origIndex: number) => {
  let currIndex = origIndex;
  let letters = "";
  while (currIndex >= 0) {
    letters = String.fromCharCode((currIndex % 26) + 65) + letters;
    currIndex = Math.floor(currIndex / 26) - 1;
  }
  return letters;
};

export const getSubmissionsByAccessId = (
  accessId: string,
  courseUsers: UserWithSectionUserType[],
  submissions: SubmissionType[],
) => {
  if (isStudentAccessId(accessId)) {
    const userId = getStudentIdFromAccessId(accessId);
    const courseUserId = courseUsers.find((u) => u.user_id === userId)?.section_user_id;
    return submissions.filter((s) => s.section_user_id === courseUserId);
  }

  if (isGroupAccessId(accessId)) {
    const pageGroupId = getGroupIdFromAccessId(accessId);
    return submissions.filter((s) => s.page_group_id === pageGroupId);
  }

  return [];
};

export const getOfficialSubmission = (submissions: SubmissionType[]) =>
  submissions.find((s) => s.official_at);

export const useNameFromPageAccessId = (
  accessId: string | undefined | null,
  pageId: string | undefined | null,
) => {
  const users = useListSelector(selectUsersInCurrentCourse);
  const pageGroups = useCustomOrdered(
    useAppSelector((s) => selectPageGroupsByPageId(s, pageId)),
    ORDER_BY_CREATED,
  );

  if (!accessId) {
    return;
  }

  if (isStudentAccessId(accessId)) {
    const studentId = getStudentIdFromAccessId(accessId);
    const user = users.find((u) => u.id === studentId);

    return user ? getUserShortName(user) : t("field.no_user");
  }

  if (isGroupAccessId(accessId)) {
    const groupId = getGroupIdFromAccessId(accessId);
    const groupIndex = pageGroups.findIndex((g) => g.id === groupId);

    return t("common.group_title", { number: groupIndex + 1 });
  }

  return null;
};

const PageTabVariantLabel: Record<PageTabVariant, string> = {
  responses: t("page_view.class_responses"),
  template: t("common.template"),
  my_submission: t("page_view.submission_one"),
  my_draft: t("page_view.workspace_one"),
  class_draft: t("page_view.class_draft"),
  student_submission: t("page_view.class_responses"),
  seeded_content: t("common.template"), // Students can see instructor seeded content if instructor changed the content after releasing.
};

export const getPageVariantLabel = (
  variant: PageTabVariant,
  isGrouped: boolean,
  isFakeSubmission: boolean = false,
) => {
  if (isGrouped) {
    if (variant === "my_submission" || isFakeSubmission) {
      return t("page_view.submission", { count: isGrouped ? 2 : 1 });
    }

    if (variant === "my_draft") {
      return t("page_view.workspace", { count: isGrouped ? 2 : 1 });
    }
  }

  return PageTabVariantLabel[isFakeSubmission ? "my_submission" : variant];
};

export const SELECTED_ACCESS_ID_PAGE_TABS = ["responses", "student_submission"];
export const SUBMISSION_PAGE_TABS = ["my_submission", "responses", "student_submission"];

export const getPresenceViewTypeFromPageTabVariant = (
  variant: PageTabVariant | null,
): PresenceViewShortType => {
  if (variant === "responses") {
    return "c";
  }
  if (variant === "template" || variant === "seeded_content") {
    return "t";
  }
  if (variant === "my_draft" || variant === "class_draft") {
    return "d";
  }
  return "o";
};

/**
 * Calculate the total points based on the scores and max points.
 * Points are calculated by taking the rubric's conversion percent
 * for each score, converting them to points, and then taking
 * the average of those points.
 */
export const calculatePointsFromOutcomeScores = (
  scores: number[],
  maxPoints: number | undefined,
  rubric: RubricType,
) => {
  if (!maxPoints) {
    return null;
  }

  if (Object.keys(scores).length === 0 || Object.values(scores).every((score) => isNaN(score))) {
    return null;
  }

  const scoresToUse = scores.filter((score) => !isNaN(score));

  const percentByScore: { [score: string]: number } = rubric.reduce(
    (acc, { score, conversion_percent }) => {
      return { ...acc, [score]: conversion_percent };
    },
    {},
  );

  const calculatePointsFromRubricScore = (score: number) =>
    percentByScore[score] * 0.01 * maxPoints || 0;

  const points = Object.values(scoresToUse).map(
    (score) => calculatePointsFromRubricScore(score) || 0,
  );
  return averageAndRound(points);
};

// This enables detecting submissions Forum collects at the due date as the server should set the
// created_at to the due_at when it collects the submission. Includes a 1 minute grace period to
// account for system timing issues as the "due now" actions by instructors does not necessarily match the due_at
// exactly.
export const isSubmissionAtDueDate = (
  submission: SubmissionType | undefined,
  dueAt: string | null,
) =>
  dueAt &&
  submission &&
  new Date(submission.created_at) >= new Date(dueAt) &&
  new Date(submission.created_at) <= addMinutes(new Date(dueAt), 1);

// Due to occasional issues with how submissions might be created slightly after the due_at by
// our submission service, we are conservative on labeling submissions as late until they are
// a minute past the due_at.
export const isSubmissionLate = (submission: SubmissionType | undefined, dueAt: string | null) =>
  dueAt && submission && new Date(submission.created_at) > addMinutes(new Date(dueAt), 1);

// Unlike isSubmissionLate, this function does not include a 1 minute grace period for UI updates immediately after the due date.
export const isPastSubmissionDueDate = (page?: PageType) => page?.due_at && isPast(page.due_at);

export const isBeforeLateSubmissionDueDate = (page: PageType) =>
  isFuture(getLateSubmissionDueDate(page));

export const getLateSubmissionDueDate = (page: PageType) =>
  addDays(page.due_at, formatDjangoDurationFieldToDaysCount(page.late_submission_interval));

export const isLateSubmissionDueDateIndefinite = (page: PageType) =>
  page.late_submission_interval === null;

export const tableDateFormat = (timestamp: string) => format(timestamp, DEFAULT_SHORT_DATE_FORMAT);

// Currently all rubrics have the same scoring system. Post-MVP this will change, but for now rely on
// the following constant.
export const OUTCOME_MAX_SCORE = 4;

export const FIRST_TAB_INDEX = 0;
export const UNKNOWN_TAB_INDEX = -1;

export const isDateWithinLastXDays = (date: string, daysAgo: number) => {
  if (!date) return false;
  return isAfter(new Date(date), subDays(new Date(), daysAgo));
};

export const topicForPage = (topics: TopicType[], activities: ActivityType[], page: PageType) => {
  return topics.find(
    (topic) =>
      activities.find((activity) => activity.id === page.activity_id)?.topic_id === topic.id,
  );
};

export const isDueSoon = (page: PageType) => {
  return isFuture(page.due_at) && differenceInDays(page.due_at, new Date()) <= 7;
};

export const intervalIsDefined = (interval: string) => interval !== EMPTY_DATE;

export const isInLateSubmissionWindow = (page: PageType) => {
  if (!intervalIsDefined(page.late_submission_interval) || !isPastSubmissionDueDate(page)) {
    return false;
  }

  // Late submissions allowed forever
  if (!page.late_submission_interval) {
    return true;
  }

  return isBeforeLateSubmissionDueDate(page);
};

export const STATUS_COLORS = {
  released: "green",
  unreleased: "gray",
  partiallyReleased: "orange",
} as const;

export type StatusColorType = (typeof STATUS_COLORS)[keyof typeof STATUS_COLORS];

export const getSubmissionTimeText = (submission?: SubmissionType): string => {
  if (!submission) {
    return null;
  }

  const { month, day, time } = monthDayAtTime(submission.created_at);
  return t("submission_menu.month_day_at_time", {
    month,
    day,
    time,
  });
};

export const getPageTypeShort = (page: PageType, activities: Record<string, ActivityType>) => {
  if (page.category === "assignment") {
    return "assignment";
  }

  if (page.category === "activity_page" && page.activity_id) {
    const activity = activities[page.activity_id];
    if (activity) {
      if (activity.category === ActivityCategory.PREP) {
        return "prep_work";
      }
      return "activities";
    }
  }

  return "other";
};

export const getPageType = (page: PageType, activities: Record<string, ActivityType>): string => {
  return t(`page_types.${getPageTypeShort(page, activities)}`);
};
